"use client"

import { useState, useCallback, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { PhoneInput } from "@/components/settings/messaging/PhoneInput"
import { cn } from "@/lib/utils"

type Channel = "whatsapp" | "telegram"

interface PhoneCaptureCardProps {
    variant: "onboarding" | "banner"
    source?: string
    onSkip?: () => void
    onPhoneCaptured?: () => void
    className?: string
}

// WhatsApp icon (green)
const WhatsAppIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={cn("h-4 w-4", className)} fill="#25D366">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
)

// Telegram icon (blue)
const TelegramIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={cn("h-4 w-4", className)} fill="#0088CC">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
)

// Toggle switch component
function ToggleSwitch({
    checked,
    onChange,
    disabled = false,
    id
}: {
    checked: boolean
    onChange: (checked: boolean) => void
    disabled?: boolean
    id?: string
}) {
    return (
        <button
            id={id}
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => onChange(!checked)}
            className={cn(
                "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2",
                checked ? "bg-gray-900" : "bg-gray-200",
                disabled && "cursor-not-allowed opacity-50"
            )}
        >
            <span
                className={cn(
                    "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ease-in-out",
                    checked ? "translate-x-5" : "translate-x-0"
                )}
            />
        </button>
    )
}

const ANIMATION_EASE = [0.22, 1, 0.36, 1] as const

export function PhoneCaptureCard({
    variant,
    source = "phone_capture_card",
    onSkip,
    onPhoneCaptured,
    className
}: PhoneCaptureCardProps) {
    const [channel, setChannel] = useState<Channel>("whatsapp")
    const [enabled, setEnabled] = useState(false)
    const [phone, setPhone] = useState("")
    const phoneCapturedRef = useRef(false)
    const phoneInputContainerRef = useRef<HTMLDivElement>(null)

    const isOnboarding = variant === "onboarding"

    // Auto-focus phone input when toggle is enabled
    useEffect(() => {
        if (!enabled) return
        // Small delay to let AnimatePresence mount the element
        const timer = setTimeout(() => {
            const input = phoneInputContainerRef.current?.querySelector("input[type='tel']")
            if (input instanceof HTMLInputElement) {
                input.focus()
            }
        }, 100)
        return () => clearTimeout(timer)
    }, [enabled])

    // Fire-and-forget phone capture
    const capturePhone = useCallback(
        (phoneValue: string) => {
            if (phoneCapturedRef.current) return
            const digits = phoneValue.replace(/\D/g, "")
            if (digits.length < 7 || isNaN(Number(digits))) return

            phoneCapturedRef.current = true

            fetch("/api/whatsapp/capture-phone", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phone: phoneValue, source, channel })
            }).catch(() => {
                phoneCapturedRef.current = false
            })

            onPhoneCaptured?.()
        },
        [source, channel, onPhoneCaptured]
    )

    const handlePhoneBlur = useCallback(() => {
        capturePhone(phone)
    }, [phone, capturePhone])

    const handlePhoneChange = useCallback(
        (value: string) => {
            setPhone(value)
            // Reset capture flag whenever phone value changes so edited numbers get re-captured
            if (phoneCapturedRef.current) {
                phoneCapturedRef.current = false
            }
        },
        []
    )

    const handleToggle = useCallback(
        (checked: boolean) => {
            setEnabled(checked)
            // Reset capture state when toggling off
            if (!checked) {
                phoneCapturedRef.current = false
            }
        },
        []
    )

    const handleChannelSwap = useCallback(() => {
        setChannel((prev) => (prev === "whatsapp" ? "telegram" : "whatsapp"))
    }, [])

    const channelName = channel === "whatsapp" ? "WhatsApp" : "Telegram"
    const ChannelIcon = channel === "whatsapp" ? WhatsAppIcon : TelegramIcon
    const altChannelName = channel === "whatsapp" ? "Telegram" : "WhatsApp"

    return (
        <div
            className={cn(
                "rounded-2xl border border-gray-100 bg-white",
                isOnboarding ? "mx-auto w-full max-w-md px-6 py-6 sm:px-8 sm:py-8" : "px-4 py-4",
                className
            )}
        >
            {/* Toggle row */}
            <div className={cn(
                "flex items-center justify-between gap-3",
                isOnboarding ? "mb-1" : ""
            )}>
                <div className="flex items-center gap-2.5 min-w-0">
                    <ChannelIcon className={isOnboarding ? "h-5 w-5" : "h-4 w-4"} />
                    <label htmlFor="phone-capture-toggle" className={cn(
                        "font-medium text-gray-900 select-none",
                        isOnboarding ? "text-[15px]" : "text-[14px]"
                    )}>
                        Get weekly growth reports on {channelName}
                    </label>
                </div>
                <ToggleSwitch checked={enabled} onChange={handleToggle} id="phone-capture-toggle" />
            </div>

            {/* Channel swap link */}
            <div className={cn(
                isOnboarding ? "mb-2 mt-1.5 pl-[30px]" : "mt-1 pl-[26px]"
            )}>
                <button
                    type="button"
                    onClick={handleChannelSwap}
                    className="text-[13px] text-gray-400 transition-colors hover:text-gray-600"
                >
                    Use {altChannelName} instead
                </button>
            </div>

            {/* Phone input - progressive disclosure */}
            <AnimatePresence mode="wait">
                {enabled && (
                    <motion.div
                        ref={phoneInputContainerRef}
                        key="phone-input"
                        initial={{ opacity: 0, y: 12, height: 0 }}
                        animate={{
                            opacity: 1,
                            y: 0,
                            height: "auto",
                            transition: {
                                height: { duration: 0.2, ease: ANIMATION_EASE },
                                opacity: { duration: 0.25, ease: ANIMATION_EASE, delay: 0.05 },
                                y: { duration: 0.25, ease: ANIMATION_EASE, delay: 0.05 }
                            }
                        }}
                        exit={{
                            opacity: 0,
                            y: 6,
                            height: 0,
                            transition: {
                                opacity: { duration: 0.15, ease: [0.4, 0, 1, 1] },
                                y: { duration: 0.15, ease: [0.4, 0, 1, 1] },
                                height: { duration: 0.2, ease: [0.4, 0, 1, 1], delay: 0.05 }
                            }
                        }}
                        className="overflow-hidden"
                    >
                        <div className={cn(
                            isOnboarding ? "pt-4" : "pt-3"
                        )}>
                            <PhoneInput
                                value={phone}
                                onChange={handlePhoneChange}
                                onBlur={handlePhoneBlur}
                                placeholder="Phone number"
                            />
                            <p className={cn(
                                "mt-2 text-[12px] text-gray-400",
                                isOnboarding ? "text-center" : ""
                            )}>
                                No spam. Unsubscribe anytime.
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Skip button (onboarding only) */}
            {isOnboarding && onSkip && (
                <div className="mt-4 text-center">
                    <button
                        type="button"
                        onClick={onSkip}
                        className="text-[13px] text-gray-400 transition-colors hover:text-gray-600"
                    >
                        Skip for now
                    </button>
                </div>
            )}
        </div>
    )
}
